import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const services = [
  {
    icon: 'ri-file-forbid-line',
    title: 'No Income Verification',
    description: 'Qualify for DSCR loans without providing tax returns, W2s, or pay stubs. Perfect for self-employed investors and those with complex income situations.',
    link: '/dscr-loan-requirements',
    gradient: 'from-blue-600 to-blue-700',
    delay: 0.1
  },
  {
    icon: 'ri-home-4-line',
    title: 'Unlimited Properties',
    description: 'Finance as many investment properties as you want. No portfolio limits or restrictions on the number of DSCR loans you can have.',
    link: '/dscr-loan-benefits',
    gradient: 'from-amber-500 to-amber-600',
    delay: 0.2
  },
  {
    icon: 'ri-speed-up-line',
    title: 'Fast Approval Process',
    description: 'Get approved quickly and close in as little as 2-3 weeks. Our streamlined DSCR loan process means faster access to financing for your investments.',
    link: '/dscr-loans',
    gradient: 'from-blue-600 to-blue-700',
    delay: 0.3
  },
  {
    icon: 'ri-percent-line',
    title: 'Competitive Rates',
    description: 'Access competitive DSCR loan rates starting at 6.5%. Compare quotes from multiple lenders to find the best terms for your investment property.',
    link: '/dscr-loan-rates',
    gradient: 'from-amber-500 to-amber-600',
    delay: 0.4
  },
  {
    icon: 'ri-building-line',
    title: 'Multiple Property Types',
    description: 'Finance single-family homes, multifamily properties, condos, townhouses, and even short-term rentals with flexible DSCR loan programs.',
    link: '/types-of-dscr-loans',
    gradient: 'from-blue-600 to-blue-700',
    delay: 0.5
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Flexible Requirements',
    description: 'DSCR loans offer flexible qualification criteria. Lower credit scores accepted, and you can qualify with DSCR ratios as low as 0.75 with some lenders.',
    link: '/best-dscr-lenders',
    gradient: 'from-amber-500 to-amber-600',
    delay: 0.6
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

export default function ServiceCards() {
  return (
    <section className="py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Why Choose DSCR Loans?
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            DSCR loans are designed specifically for real estate investors who want to grow their portfolio 
            without the hassle of traditional mortgage requirements.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ y: -8, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <Link
                to={service.link}
                className="group block bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-slate-200 hover:border-blue-300 h-full"
              >
                <motion.div
                  whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                  className={`w-16 h-16 bg-gradient-to-br ${service.gradient} rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-xl`}
                >
                  <i className={`${service.icon} text-3xl text-white`}></i>
                </motion.div>

                <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-blue-700 transition-colors">
                  {service.title}
                </h3>

                <p className="text-slate-600 leading-relaxed mb-4">
                  {service.description}
                </p>

                <div className="flex items-center text-blue-600 font-semibold group-hover:text-amber-600 transition-colors">
                  Learn More
                  <motion.i
                    className="ri-arrow-right-line ml-2"
                    animate={{ x: [0, 5, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                  ></motion.i>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
